import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import { LocaleToLanguage } from '@/languages/messages'
import languagesJsonData from '@/../../shared/languages.json';
import { LanguagesManager } from '@/languages/languages';
import { PhBookOpenText, PhChats, PhPersonSimpleTaiChi, PhLightbulb, PhFilmSlate, PhPenNib, PhBook } from '@phosphor-icons/vue';
import type { AgentTemplate } from '@/types/agents';
import type { Agent } from '@/types/model';
import { getPendingRedirectClickId } from '@/practice/redirectAttribution';


export const FSRS_TARGET_RETENTION = 0.9;
export const OPEN_ENDED_KICKSTART_ID_HEX = "00000000000000000000000000000000";
export const INTRO_LESSON_KICKSTART_ID_HEX = "00000000000000000000000000000001";
export const LOG_SENSITIVE_DATA =
  import.meta.env.DEV || ['local', 'localDevelopment'].includes(import.meta.env.VITE_ISSEN_ENV);


export const getDefaultSourceLanguage = (): string => {
  const browserLang = navigator.language;
  const sourceLocale = browserLang.split('-')[0];
  const sourceLangId = LocaleToLanguage[sourceLocale];
  const savedLang = localStorage.getItem("guiLanguage");
  if (savedLang && Object.values(Language).includes(savedLang as Language)) {
    return savedLang;
  }
  if (sourceLangId && Object.values(Language).includes(sourceLangId as Language)) {
    return sourceLangId;
  }
  
  return Language.ENGLISH;
}

export function cleanString(input: string): string {
    return input
        .toLowerCase()                          // Convert to lowercase
        .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '')  // Remove punctuation
        .replace(/\s+/g, '');                   // Remove all spaces
}

export async function createAudioBuffer(
    context: BaseAudioContext,
    float32Array: Float32Array,
    channels: number
): Promise<AudioBuffer> {
    const audioBuffer = context.createBuffer(channels, float32Array.length / channels, context.sampleRate);

    for (let channel = 0; channel < channels; channel++) {
        const channelData = audioBuffer.getChannelData(channel);
        for (let i = 0; i < channelData.length; i++) {
            channelData[i] = float32Array[i * channels + channel];
        }
    }

    return audioBuffer;
}

export function audioBufferToFloat32Array(audioBuffer: AudioBuffer): Float32Array {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const length = audioBuffer.length;

    // Initialize a Float32Array to hold the interleaved data
    const interleaved = new Float32Array(length * numberOfChannels);

    // Interleave the channel data
    for (let channel = 0; channel < numberOfChannels; channel++) {
        const channelData = audioBuffer.getChannelData(channel);
        for (let i = 0; i < channelData.length; i++) {
            interleaved[i * numberOfChannels + channel] = channelData[i];
        }
    }

    return interleaved;
}

export async function resampleAudioBuffer(
    audioBuffer: AudioBuffer,
    targetSampleRate: number
): Promise<AudioBuffer> {
    const offlineContext = new OfflineAudioContext(
        audioBuffer.numberOfChannels,
        audioBuffer.duration * targetSampleRate,
        targetSampleRate
    );

    const bufferSource = offlineContext.createBufferSource();
    bufferSource.buffer = audioBuffer;
    bufferSource.connect(offlineContext.destination);
    bufferSource.start(0);

    return await offlineContext.startRendering();
}

export async function audioToWebSocketData(audioContext: AudioContext, float32Data: Float32Array, sampleRate: number): Promise<ArrayBuffer> {
    const audioBuffer = await createAudioBuffer(audioContext, float32Data, 1);
    const resampledBuffer = await resampleAudioBuffer(audioBuffer, sampleRate);
    const resampledData = audioBufferToFloat32Array(resampledBuffer);
    return float32ToByteArray(resampledData);
}

export function float32ToByteArray(float32Array: Float32Array): Uint8Array {
    // Create an ArrayBuffer with the same byte length as the float32Array
    const buffer = new ArrayBuffer(float32Array.length * 4); // 4 bytes per float32
    // Create a DataView to manipulate the ArrayBuffer
    const view = new DataView(buffer);

    // Store each float32 value as bytes
    for (let i = 0; i < float32Array.length; i++) {
        view.setFloat32(i * 4, float32Array[i], true); // true for little-endian
    }

    return new Uint8Array(buffer);
}

export const createAudioLink = (word: string, downloadBlob: Blob) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(downloadBlob);
  link.download = word + ".mp3";
  link.click();
  link.remove(); // Clean up the DOM
}

export const getErrorMessage = (event: any) => {
  if (event instanceof Event) {
    return 'A WebSocket error occurred.';
  } else if (event instanceof ErrorEvent) {
    return `Error message: ${event.message}`;
  } else if (typeof event === 'string') {
    return event;
  } else {
    return 'An unknown WebSocket error occurred.';
  }
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const convertRubyToRaw = (text: string | null): string => {
  if (!text) return '';

  // Remove rt tags and their content first
  text = text.replace(/<rt>.*?<\/rt>/g, '');
  
  // Remove ruby tags but keep their base content
  text = text.replace(/<ruby>|<\/ruby>/g, '');
  
  // Remove any remaining ruby or rt tags
  text = text.replace(/<\/?ruby>|<\/?rt>/g, '');
  return text;
};

const DISPLAY_HTML_ALLOWED_TAGS = new Set(['BR', 'RT', 'RUBY', 'STRONG']);

export const sanitizeDisplayHtml = (html: string | null | undefined): string => {
  if (html === null || html === undefined || html.length === 0) {
    return '';
  }

  const documentNode = new DOMParser().parseFromString('', 'text/html');
  const container = documentNode.createElement('div');
  container.innerHTML = html;
  const sanitizeChildren = (parent: Node): void => {
    for (const child of Array.from(parent.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        continue;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) {
        child.remove();
        continue;
      }

      const element = child as HTMLElement;
      if (!DISPLAY_HTML_ALLOWED_TAGS.has(element.tagName)) {
        sanitizeChildren(element);
        element.replaceWith(...Array.from(element.childNodes));
        continue;
      }

      for (const attribute of Array.from(element.attributes)) {
        element.removeAttribute(attribute.name);
      }
      sanitizeChildren(element);
    }
  };

  sanitizeChildren(container);
  return container.innerHTML;
};

export const supportsRubyCharacters = (language: string) => {
    return ['japanese', 'mandarin_chinese', 'cantonese'].includes(language);
}

export const hasMultipleTransliterationStyles = (language: string) => {
    return language === 'mandarin_chinese';
}

export const getTransliterationOptions = (language: string): string[] => {
    if (language === 'mandarin_chinese') return ['pinyin', 'zhuyin'];
    if (language === 'japanese') return ['hiragana'];
    if (language === 'cantonese') return ['jyutping'];
    return [];
}

export const getDefaultTransliterationStyle = (language: string): string | null => {
    if (language === 'mandarin_chinese') return 'pinyin';
    if (language === 'japanese') return 'hiragana';
    if (language === 'cantonese') return 'jyutping';
    return null;
}



export const boldedSentence = (sentence: string, word: string) => {
  if (!sentence || !word) return null;

  const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const rubyRegex = new RegExp(`<ruby>[^<]*?(${escapedWord})[^<]*?<rt>.*?</rt></ruby>`, 'g');
  const hasRuby = rubyRegex.test(sentence);
  
  if (hasRuby) {
    return sentence.replace(
      new RegExp(`(<ruby>[^<]*?)(${escapedWord})([^<]*?<rt>.*?</rt></ruby>)`, 'g'), 
      '$1<strong>$2</strong>$3'
    );
  } else {
    return sentence.replace(new RegExp(`(${escapedWord})`, 'g'), '<strong>$1</strong>');
  }
};


export enum Language {
    AFRIKAANS = "afrikaans",
    ARABIC = "arabic",
    ARMENIAN = "armenian",
    AZERBAIJANI = "azerbaijani",
    BELARUSIAN = "belarusian",
    BOSNIAN = "bosnian",
    BULGARIAN = "bulgarian",
    CATALAN = "catalan",
    MANDARIN_CHINESE = "mandarin_chinese",
    CANTONESE = "cantonese",
    CROATIAN = "croatian",
    CZECH = "czech",
    DANISH = "danish",
    DUTCH = "dutch",
    ENGLISH = "english",
    ESTONIAN = "estonian",
    FINNISH = "finnish",
    FRENCH = "french",
    GALICIAN = "galician",
    GERMAN = "german",
    GREEK = "greek",
    GUJARATI = "gujarati",
    HEBREW = "hebrew",
    HINDI = "hindi",
    HUNGARIAN = "hungarian",
    ICELANDIC = "icelandic",
    INDONESIAN = "indonesian",
    ITALIAN = "italian",
    JAPANESE = "japanese",
    KANNADA = "kannada",
    KAZAKH = "kazakh",
    KOREAN = "korean",
    LATVIAN = "latvian",
    LITHUANIAN = "lithuanian",
    MACEDONIAN = "macedonian",
    MALAY = "malay",
    MARATHI = "marathi",
    MAORI = "maori",
    NEPALI = "nepali",
    NORWEGIAN = "norwegian",
    PERSIAN = "persian",
    POLISH = "polish",
    PORTUGUESE = "portuguese",
    ROMANIAN = "romanian",
    RUSSIAN = "russian",
    SERBIAN = "serbian",
    SLOVAK = "slovak",
    SLOVENIAN = "slovenian",
    SPANISH = "spanish",
    SWAHILI = "swahili",
    SWEDISH = "swedish",
    TAGALOG = "tagalog",
    TAMIL = "tamil",
    THAI = "thai",
    TURKISH = "turkish",
    UKRAINIAN = "ukrainian",
    URDU = "urdu",
    VIETNAMESE = "vietnamese",
    WELSH = "welsh"
}

// toLocaleDateString builds a new formatter on every call, which added up
// over the home's session timeline.
const sessionDateFormat = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

// Add timeAgo function
export function timeAgo(timestamp: number): string {
  return sessionDateFormat.format(new Date(timestamp * 1000));
}

// Add formatDuration function
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return `${minutes}m${remainingSeconds > 0 ? ` ${remainingSeconds}s` : ''}`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h${remainingMinutes > 0 ? ` ${remainingMinutes}m` : ''}`;
}

// Get icon component based on session type
export const getIconForSessionType = (type?: string): typeof PhBook | typeof PhBookOpenText | typeof PhPersonSimpleTaiChi | typeof PhLightbulb | typeof PhFilmSlate | typeof PhPenNib | typeof PhChats => {
  if (!type) return PhBook;
  
  switch (type.toLowerCase()) {
    case 'grammar':
      return PhBookOpenText;
    case 'student_life':
      return PhPersonSimpleTaiChi;
    case 'topic':
      return PhLightbulb;
    case 'scenario':
      return PhFilmSlate;
    case 'saved_words':
      return PhPenNib;
    case 'open_ended':
      return PhChats;
    default:
      return PhBook;
  }
};

// Get colors based on unit position
export const getColorsForUnit = (unitPosition: number): { primary: string; subtle: string; muted: string } => {
  const colors = [
    { primary: 'var(--curriculum-unit-1-primary)', subtle: 'var(--curriculum-unit-1-subtle)', muted: 'var(--curriculum-unit-1-muted)' },
    { primary: 'var(--curriculum-unit-2-primary)', subtle: 'var(--curriculum-unit-2-subtle)', muted: 'var(--curriculum-unit-2-muted)' },
    { primary: 'var(--curriculum-unit-3-primary)', subtle: 'var(--curriculum-unit-3-subtle)', muted: 'var(--curriculum-unit-3-muted)' },
    { primary: 'var(--curriculum-unit-4-primary)', subtle: 'var(--curriculum-unit-4-subtle)', muted: 'var(--curriculum-unit-4-muted)' },
    { primary: 'var(--curriculum-unit-5-primary)', subtle: 'var(--curriculum-unit-5-subtle)', muted: 'var(--curriculum-unit-5-muted)' },
    { primary: 'var(--curriculum-unit-6-primary)', subtle: 'var(--curriculum-unit-6-subtle)', muted: 'var(--curriculum-unit-6-muted)' },
  ];
  
  return colors[unitPosition % colors.length];
};

// Get colors based on session type
export const getColorsForSessionType = (
  type?: string, 
  isLessonMode: boolean = false, 
  unitPosition?: number | null
): { primary: string; muted: string; subtle?: string } => {
  // If in lesson mode and has unit position, use curriculum colors
  if (isLessonMode && unitPosition !== null && unitPosition !== undefined) {
    const colors = getColorsForUnit(unitPosition);
    return { primary: colors.primary, muted: colors.muted, subtle: colors.subtle };
  }
  
  if (!type) {
    return {
      primary: 'var(--curriculum-default-primary)',
      muted: 'var(--curriculum-default-muted)',
      subtle: 'var(--curriculum-default-subtle)',
    };
  }
  
  switch (type.toLowerCase()) {
    case 'grammar':
      const grammarColors = getColorsForUnit(0);
      return { primary: grammarColors.primary, muted: grammarColors.muted, subtle: grammarColors.subtle };
    case 'student_life':
      const studentLifeColors = getColorsForUnit(1);
      return { primary: studentLifeColors.primary, muted: studentLifeColors.muted, subtle: studentLifeColors.subtle };
    case 'topic':
      const topicColors = getColorsForUnit(2);
      return { primary: topicColors.primary, muted: topicColors.muted, subtle: topicColors.subtle };
    case 'scenario':
      const scenarioColors = getColorsForUnit(3);
      return { primary: scenarioColors.primary, muted: scenarioColors.muted, subtle: scenarioColors.subtle };
    case 'saved_words':
      const savedWordsColors = getColorsForUnit(4);
      return { primary: savedWordsColors.primary, muted: savedWordsColors.muted, subtle: savedWordsColors.subtle };
    default:
      return {
        primary: 'var(--curriculum-default-primary)',
        muted: 'var(--curriculum-default-muted)',
        subtle: 'var(--curriculum-default-subtle)',
      };
  }
};

/**
 * Creates a trial user and logs them in
 * @param API - The PracticeAPI instance
 * @returns Promise<{ success: boolean, username: string }>
 */
export const createAndLoginUser = async (API: any): Promise<{ success: boolean, username: string }> => {
  try {
    // Generate temporary username if none provided
    const finalUsername = `temp_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    const passwordBytes = new Uint8Array(32);
    crypto.getRandomValues(passwordBytes);
    const trialPassword = Array.from(passwordBytes, (byte: number) => byte.toString(16).padStart(2, "0")).join("");
    
    // Get timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Create user
    const createUserResult = { data: { value: null }, error: { value: null }, isFetching: { value: false } };
    await API.create_user(finalUsername, trialPassword, timezone, "", getPendingRedirectClickId(), createUserResult);
    
    if (createUserResult.error.value) {
      console.error("Failed to create user:", createUserResult.error.value);
      return { success: false, username: finalUsername };
    }
    
    // Login user
    const loginResult = { data: { value: null }, error: { value: null }, isFetching: { value: false } };
    await API.login(finalUsername, trialPassword, loginResult);
    
    if (!loginResult.data.value || !(loginResult.data.value as any)?.success) {
      console.error("Failed to login user:", loginResult.error.value);
      return { success: false, username: finalUsername };
    }
    
    return { success: true, username: finalUsername };
  } catch (error) {
    console.error("Error creating and logging in user:", error);
    return { success: false, username: "" };
  }
};

/**
 * Checks if an agent should be allowed for the user based on source language and proficiency
 */
export const isAgentAllowedForUser = (
  agent: AgentTemplate,
  targetLanguage: string | null | undefined,
  sourceLanguage: string | null | undefined,
  proficiencyLevel: string | null | undefined,
  isCreator: boolean | null | undefined
): boolean => {
  if (agent.language !== targetLanguage) return false;
  // Gemini TTS gives target-language phrases inside source-language sentences a
  // source-language accent, so Gemini tutors stay experimental for novice students.
  const isExperimental = agent.experimental === true || (agent.voiceService === 'gemini' && proficiencyLevel === 'novice');
  if (isExperimental && isCreator !== true) return false;
  if (agent.deprecated === true) return false;
  return true;
};

export const ENGLISH_MALE_RECOMMENDATION = 'english-theodore';

/**
 * Gets the recommended agents for a user.
 */
export const getRecommendedAgentsForUser = (userId: string | null | undefined): string[] => {
  // Default recommended agents (one male + one female per prioritized language).
  return [
    "english-violet", ENGLISH_MALE_RECOMMENDATION,
    "spanish-santiago", "spanish-valentina",
    "french-céleste", "french-étienne",
    "italian-lorenzo",
    "portuguese-camila-brazil", "portuguese-tiago",
    "german-johanna", "german-maximilian",
    "dutch-sander",
    "japanese-aoi", "japanese-kyo",
    "mandarin_chinese-ying", "mandarin_chinese-chenhao",
    "turkish-kerem", "turkish-zeynep",
    "korean-hae-won", "korean-tae-hyung",
  ];
};

/**
 * Orders agent templates for display, prioritizing:
 * 1. Current language tutors: used tutors in recency order + unused tutors
 * 2. Other language tutors: in recency order (only those the user has used)
 */
export const orderAgentTemplatesForLanguage = (
  allAgentTemplates: AgentTemplate[],
  userAgents: Agent[],
  targetLanguage: string | null | undefined,
  selectedAgentTemplateId?: string | null,
  sourceLanguage?: string | null,
  proficiencyLevel?: string | null,
  isCreator?: boolean | null,
  userId?: string | null
): AgentTemplate[] => {
  if (!allAgentTemplates || allAgentTemplates.length === 0) {
    return [];
  }

  const userAgentTemplateIds = new Set(userAgents.map(agent => agent.agentTemplateId));
  
  if (targetLanguage === null || targetLanguage === undefined || targetLanguage === "") {
    return allAgentTemplates.filter(template =>
      template.experimental !== true ||
      isCreator === true ||
      template.agentTemplateId === selectedAgentTemplateId ||
      userAgentTemplateIds.has(template.agentTemplateId)
    );
  }
  
  // Current language tutors that the user has used (in recency order from userAgents)
  const currentLanguageUsedTemplates = userAgents
    .filter(agent => agent.language === targetLanguage)
    .map(agent => allAgentTemplates.find(template => template.agentTemplateId === agent.agentTemplateId))
    .filter(template => template !== undefined) as AgentTemplate[];
  
  const currentLanguageUsedIds = new Set(currentLanguageUsedTemplates.map(t => t.agentTemplateId));
  
  // Ensure the currently selected agent template is always included at the front if it's for current language
  if (selectedAgentTemplateId) {
    const selectedTemplate = allAgentTemplates.find(template => template.agentTemplateId === selectedAgentTemplateId);
    if (
      selectedTemplate &&
      selectedTemplate.language === targetLanguage &&
      !currentLanguageUsedIds.has(selectedAgentTemplateId)
    ) {
      currentLanguageUsedTemplates.unshift(selectedTemplate);
      currentLanguageUsedIds.add(selectedAgentTemplateId);
    }
  }
  
  // Current language tutors that the user hasn't used yet
  const currentLanguageUnusedTemplates = allAgentTemplates.filter(
    template => template.language === targetLanguage && 
                !currentLanguageUsedIds.has(template.agentTemplateId) &&
                isAgentAllowedForUser(template, targetLanguage, sourceLanguage, proficiencyLevel, isCreator)
  );
  
  const recommendedAgents = getRecommendedAgentsForUser(userId);
  
  // Other language tutors that the user has used (in recency order from userAgents)
  const otherLanguageUsedTemplates = userAgents
    .filter(agent => agent.language !== targetLanguage)
    .map(agent => allAgentTemplates.find(template => template.agentTemplateId === agent.agentTemplateId))
    .filter(template => template !== undefined) as AgentTemplate[];
  
  const currentLanguageTemplates = [
    ...currentLanguageUsedTemplates,
    ...currentLanguageUnusedTemplates,
  ];

  const selectedTemplate = selectedAgentTemplateId
    ? currentLanguageTemplates.find(template => template.agentTemplateId === selectedAgentTemplateId)
    : undefined;

  const remainingCurrentLanguage = selectedTemplate
    ? currentLanguageTemplates.filter(template => template.agentTemplateId !== selectedTemplate.agentTemplateId)
    : currentLanguageTemplates;

  const recommendedCurrentLanguage = remainingCurrentLanguage.filter(template =>
    recommendedAgents.includes(template.agentTemplateId)
  );
  const otherCurrentLanguage = remainingCurrentLanguage.filter(template =>
    !recommendedAgents.includes(template.agentTemplateId)
  );

  return [
    ...(selectedTemplate ? [selectedTemplate] : []),
    ...recommendedCurrentLanguage,
    ...otherCurrentLanguage,
    ...otherLanguageUsedTemplates
  ];
};

